import { Link } from "react-router-dom";
import { ArrowRight, Check, Sparkles } from "lucide-react";
import { Reveal } from "@/components/public/Reveal";
import { Shot } from "@/components/public/Shot";
import { TOUR } from "@/lib/publicContent";

export const Eyebrow = ({ children }) => <p className="font-mono text-xs uppercase tracking-[0.18em] text-sky-300">{children}</p>;

export const PlanNote = ({ text, testid }) => (
  <p className="mt-6 inline-flex items-center gap-2 rounded-full border border-sky-400/20 bg-sky-400/[0.07] px-3 py-1 text-xs text-sky-200" data-testid={testid}>
    <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />{text}
  </p>
);

function Points({ points, testid }) {
  return (
    <ul className="mt-6 space-y-3" data-testid={testid}>
      {points.map((p) => (
        <li key={p} className="flex gap-3 text-sm text-neutral-300"><Check className="h-4 w-4 mt-0.5 text-sky-300 shrink-0" aria-hidden="true" />{p}</li>
      ))}
    </ul>
  );
}

export function FeatureRow({ item: t, index, heading: H = "h3", testid = `tour-${t.id}` }) {
  return (
    <article className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center" aria-labelledby={`${testid}-title`} data-testid={testid}>
      <Reveal className={`lg:col-span-5 ${index % 2 ? "lg:order-last" : ""}`}>
        <p className="font-mono text-xs tracking-wide text-sky-300"><span className={t.kicker ? "text-neutral-600" : ""}>0{index + 1}</span>{t.kicker && <>&nbsp;&nbsp;{t.kicker}</>}</p>
        <H id={`${testid}-title`} className="font-display text-3xl sm:text-4xl font-semibold tracking-[-0.025em] leading-[1.08] mt-4">{t.title}</H>
        <p className="mt-5 text-neutral-400 leading-relaxed">{t.text}</p>
        {t.points && <Points points={t.points} testid={`${testid}-points`} />}
        {t.plan && <PlanNote text={t.plan} testid={`${testid}-plan`} />}
        {t.link && (
          <Link to={t.link[0]} className="mt-7 inline-flex items-center gap-1.5 text-sm text-sky-400 hover:text-sky-300 rounded-md" data-testid={`${testid}-link`}>
            {t.link[1]} <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        )}
      </Reveal>
      <Reveal delay={120} className="lg:col-span-7">
        <Shot {...t.shot} testid={`${testid}-shot`} />
      </Reveal>
    </article>
  );
}

export function HomeTour() {
  return (
    <section id="tour" tabIndex={-1} className="scroll-mt-20 outline-none max-w-7xl mx-auto px-5 sm:px-8 mt-32 sm:mt-44" aria-labelledby="tour-title" data-testid="home-tour">
      <Reveal className="max-w-3xl">
        <Eyebrow>How it works</Eyebrow>
        <h2 id="tour-title" className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.025em] leading-[1.05] mt-4">Everything you need to study</h2>
        <p className="mt-5 text-base sm:text-lg text-neutral-400 leading-relaxed" data-testid="stuck-copy">Stuck on homework the night before it's due? Upload it and get an explanation that teaches the method.</p>
      </Reveal>
      <div className="mt-20 sm:mt-28 space-y-28 sm:space-y-40">
        {TOUR.map((t, i) => <FeatureRow key={t.id} item={t} index={i} />)}
      </div>
    </section>
  );
}
